// ActionSystem.ts - 动作系统
// 管理所有可执行动作：喂食、浇水、扫地、对话、休息等

import { GameState, PlayerAttributes } from './GameState.js';
import { StorySystem } from './StorySystem.js';
import { FloatingTextSystem } from './FloatingText.js';

// 动作类型
export type ActionType = 
  | 'feed' | 'water' | 'sweep' | 'talk' | 'rest' 
  | 'chop_wood' | 'tidy' | 'play' | 'help' | 'enter_house'
  | 'look' | 'move' | 'pet';

// 动作目标
export interface ActionTarget {
  type: string;
  id: string;
  label: string;
  x: number;
  y: number;
}

// 动作定义
export interface Action {
  type: ActionType;
  target?: ActionTarget;
  duration: number; // 执行时间（秒）
  cooldown: number; // 冷却时间（秒）
  energyCost: number; // 消耗体力
  rewards: {
    happiness?: number;
    memoryPoints?: number;
    coins?: number;
  };
  requirements?: {
    minEnergy?: number;
    flag?: string;
  };
}

// 动作结果
export interface ActionResult {
  success: boolean;
  message: string;
  rewards?: {
    happiness?: number;
    memoryPoints?: number;
    coins?: number;
  };
  effects?: string[];
}

// 欢欢情感状态
export type HuanhuanEmotion = 'happy' | 'tired' | 'curious' | 'sad' | 'excited' | 'content';

// 欢欢状态
export interface HuanhuanState {
  emotion: HuanhuanEmotion;
  energy: number; // 0-100
  hunger: number; // 0-100 (0=饿, 100=饱)
  taskHint: string;
}

// 丹丹状态
export interface DandanState {
  mood: 'happy' | 'normal' | 'bored' | 'excited';
  activity: 'following' | 'patrolling' | 'sleeping' | 'looking_for_bone' | 'chasing_butterfly' | 'playing';
  playCount: number;
  lastInteraction: number;
}

// 奶奶状态
export interface GrandmaState {
  currentActivity: 'fan' | 'nap' | 'cooking' | 'knitting' | 'chatting';
  activityTimer: number;
  availableActions: string[];
}

// 爷爷状态
export interface DayeState {
  currentActivity: 'smoking' | 'reading' | 'tea' | 'chess' | 'chatting';
  activityTimer: number;
  availableActions: string[];
  storyQueue: string[];
}

export class ActionSystem {
  private gameState: GameState;
  private storySystem: StorySystem;
  private floatingText: FloatingTextSystem | null = null;
  
  // 角色状态
  private huanhuanState: HuanhuanState;
  private dandanState: DandanState;
  private grandmaState: GrandmaState;
  private dayeState: DayeState;
  
  // 动作冷却
  private actionCooldowns: Map<string, number> = new Map();
  private lastUpdate: number = Date.now();
  
  // 回调
  private onHuanhuanStateChange?: (state: HuanhuanState) => void;
  private onActionComplete?: (result: ActionResult) => void;

  constructor(gameState: GameState, storySystem: StorySystem) {
    this.gameState = gameState;
    this.storySystem = storySystem;
    
    // 初始化欢欢状态
    this.huanhuanState = {
      emotion: 'content',
      energy: 80,
      hunger: 70,
      taskHint: '点击地面移动，点击物体互动'
    };
    
    // 初始化丹丹状态
    this.dandanState = {
      mood: 'happy',
      activity: 'following',
      playCount: 0,
      lastInteraction: Date.now()
    };
    
    // 初始化奶奶状态
    this.grandmaState = {
      currentActivity: 'fan',
      activityTimer: 0,
      availableActions: ['talk', 'help_cooking', 'listen_story']
    };
    
    // 初始化爷爷状态
    this.dayeState = {
      currentActivity: 'smoking',
      activityTimer: 0,
      availableActions: ['talk', 'help_chores', 'listen_story'],
      storyQueue: ['young_days', 'village_history', 'farming_tips']
    };
    
    // 启动状态更新循环
    this.startStateUpdateLoop();
  }

  // 设置飘字系统
  setFloatingText(floatingText: FloatingTextSystem): void {
    this.floatingText = floatingText;
  }

  // 设置回调
  setCallbacks(callbacks: {
    onHuanhuanStateChange?: (state: HuanhuanState) => void;
    onActionComplete?: (result: ActionResult) => void;
  }): void {
    this.onHuanhuanStateChange = callbacks.onHuanhuanStateChange;
    this.onActionComplete = callbacks.onActionComplete;
  }

  // 状态更新循环
  private startStateUpdateLoop(): void {
    setInterval(() => {
      const now = Date.now();
      const dt = (now - this.lastUpdate) / 1000;
      this.lastUpdate = now;
      
      this.updateCooldowns(dt);
      this.updateCharacterStates(dt);
      this.updateHuanhuanState(dt);
    }, 1000);
  }

  // 更新冷却时间
  private updateCooldowns(dt: number): void {
    this.actionCooldowns.forEach((time, key) => {
      if (time > 0) {
        this.actionCooldowns.set(key, Math.max(0, time - dt));
      }
    });
  }

  // 更新角色状态
  private updateCharacterStates(dt: number): void {
    // 更新奶奶状态
    this.grandmaState.activityTimer += dt;
    if (this.grandmaState.activityTimer > 30) {
      this.switchGrandmaActivity();
      this.grandmaState.activityTimer = 0;
    }
    
    // 更新爷爷状态
    this.dayeState.activityTimer += dt;
    if (this.dayeState.activityTimer > 30) {
      this.switchDayeActivity();
      this.dayeState.activityTimer = 0;
    }
    
    // 更新丹丹AI
    this.updateDandanAI(dt);
  }

  // 更新欢欢状态
  private updateHuanhuanState(dt: number): void {
    // 缓慢恢复体力
    if (this.huanhuanState.energy < 100) {
      this.huanhuanState.energy = Math.min(100, this.huanhuanState.energy + dt * 0.5);
    }
    
    // 缓慢消耗饱食度
    if (this.huanhuanState.hunger > 0) {
      this.huanhuanState.hunger = Math.max(0, this.huanhuanState.hunger - dt * 0.3);
    }
    
    // 根据饱食度更新情感
    if (this.huanhuanState.hunger < 20) {
      this.huanhuanState.emotion = 'tired';
    } else if (this.huanhuanState.energy > 80 && this.huanhuanState.hunger > 60) {
      this.huanhuanState.emotion = 'happy';
    }
    
    this.notifyHuanhuanStateChange();
  }

  // 更新丹丹AI
  private updateDandanAI(dt: number): void {
    const timeSinceLastInteraction = (Date.now() - this.dandanState.lastInteraction) / 1000;
    
    // 如果很久没互动，变得无聊
    if (timeSinceLastInteraction > 60 && this.dandanState.mood === 'happy') {
      this.dandanState.mood = 'bored';
    }
    
    // 随机切换活动
    if (Math.random() < 0.01) {
      const activities: DandanState['activity'][] = ['patrolling', 'sleeping', 'looking_for_bone', 'chasing_butterfly'];
      this.dandanState.activity = activities[Math.floor(Math.random() * activities.length)];
    }
  }

  // 切换奶奶活动
  private switchGrandmaActivity(): void {
    const activities: GrandmaState['currentActivity'][] = ['fan', 'nap', 'cooking', 'knitting'];
    const currentIndex = activities.indexOf(this.grandmaState.currentActivity);
    let newIndex;
    do {
      newIndex = Math.floor(Math.random() * activities.length);
    } while (newIndex === currentIndex);
    
    this.grandmaState.currentActivity = activities[newIndex];
  }

  // 切换爷爷活动
  private switchDayeActivity(): void {
    const activities: DayeState['currentActivity'][] = ['smoking', 'reading', 'tea', 'chess'];
    const currentIndex = activities.indexOf(this.dayeState.currentActivity);
    let newIndex;
    do {
      newIndex = Math.floor(Math.random() * activities.length);
    } while (newIndex === currentIndex);
    
    this.dayeState.currentActivity = activities[newIndex];
  }

  // 执行动作
  executeAction(actionType: ActionType, target?: ActionTarget): ActionResult {
    const cooldownKey = `${actionType}_${target?.id || 'general'}`;
    const cooldown = this.actionCooldowns.get(cooldownKey) || 0;
    
    if (cooldown > 0) {
      return {
        success: false,
        message: `还需要等待 ${Math.ceil(cooldown)} 秒`
      };
    }

    let result: ActionResult;
    
    switch (actionType) {
      case 'feed':
        result = this.actionFeed(target);
        break;
      case 'water':
        result = this.actionWater(target);
        break;
      case 'sweep':
        result = this.actionSweep(target);
        break;
      case 'talk':
        result = this.actionTalk(target);
        break;
      case 'rest':
        result = this.actionRest();
        break;
      case 'chop_wood':
        result = this.actionChopWood(target);
        break;
      case 'tidy':
        result = this.actionTidy(target);
        break;
      case 'play':
        result = this.actionPlay(target);
        break;
      case 'help':
        result = this.actionHelp(target);
        break;
      case 'enter_house':
        result = this.actionEnterHouse();
        break;
      case 'pet':
        result = this.actionPet(target);
        break;
      default:
        result = { success: false, message: '未知动作' };
    }

    if (result.success) {
      // 设置冷却
      this.actionCooldowns.set(cooldownKey, 5);
      
      // 通知故事系统
      this.storySystem.checkAndUpdateObjective(actionType, target?.type || '');
      
      // 显示飘字
      if (this.floatingText && result.rewards) {
        if (result.rewards.happiness) {
          this.floatingText.spawn(target?.x || 400, (target?.y || 300) - 50, `快乐 +${result.rewards.happiness}`, 'exp');
        }
        if (result.rewards.memoryPoints) {
          this.floatingText.spawn(target?.x || 400, (target?.y || 300) - 80, `回忆 +${result.rewards.memoryPoints}`, 'info');
        }
      }
      
      if (this.onActionComplete) {
        this.onActionComplete(result);
      }
    }

    return result;
  }

  // 喂食动作
  private actionFeed(target?: ActionTarget): ActionResult {
    if (!target) {
      return { success: false, message: '没有指定喂食目标' };
    }

    if (this.huanhuanState.energy < 10) {
      return { success: false, message: '太累了，先休息一下吧' };
    }

    this.huanhuanState.energy -= 10;
    this.huanhuanState.hunger = Math.min(100, this.huanhuanState.hunger + 20);

    const messages: Record<string, string> = {
      'chicken': '🌾 撒下一把小米，小鸡们开心地吃着~',
      'dandan': '🦴 给丹丹一根骨头，它开心地啃着~',
      'cat_pet': '🐟 给猫咪一条小鱼，它优雅地品尝着~'
    };

    const rewards = target.type === 'dandan' 
      ? { happiness: 5, memoryPoints: 2 }
      : { happiness: 3 };

    return {
      success: true,
      message: messages[target.type] || '喂食完成',
      rewards
    };
  }

  // 浇水动作
  private actionWater(target?: ActionTarget): ActionResult {
    if (!target) {
      return { success: false, message: '没有指定浇水目标' };
    }

    if (this.huanhuanState.energy < 15) {
      return { success: false, message: '太累了，先休息一下吧' };
    }

    this.huanhuanState.energy -= 15;

    return {
      success: true,
      message: '💧 浇过水的花草更加精神了~',
      rewards: { happiness: 4, memoryPoints: 1 }
    };
  }

  // 扫地动作
  private actionSweep(target?: ActionTarget): ActionResult {
    if (this.huanhuanState.energy < 20) {
      return { success: false, message: '太累了，先休息一下吧' };
    }

    this.huanhuanState.energy -= 20;

    return {
      success: true,
      message: '🧹 院子变得干净整洁了~',
      rewards: { happiness: 5, memoryPoints: 2 }
    };
  }

  // 对话动作
  private actionTalk(target?: ActionTarget): ActionResult {
    if (!target) {
      return { success: false, message: '没有对话目标' };
    }

    const messages: Record<string, { text: string; rewards: any; memoryChance: number }> = {
      'grandma': {
        text: '👵 和奶奶聊着家常，听她讲过去的故事...',
        rewards: { happiness: 8, memoryPoints: 5 },
        memoryChance: 0.3
      },
      'daye': {
        text: '👴 爷爷讲起了年轻时的故事...',
        rewards: { happiness: 6, memoryPoints: 5 },
        memoryChance: 0.4
      },
      'neighbor': {
        text: '👋 和邻居聊了一会儿天...',
        rewards: { happiness: 4, memoryPoints: 3 },
        memoryChance: 0.2
      },
      'dandan': {
        text: '🐕 虽然丹丹不会说话，但你们用眼神交流...',
        rewards: { happiness: 10, memoryPoints: 3 },
        memoryChance: 0.1
      }
    };

    const talkData = messages[target.type];
    if (!talkData) {
      return { success: false, message: '无法与这个目标对话' };
    }

    // 随机触发记忆碎片
    if (Math.random() < talkData.memoryChance) {
      // 尝试解锁记忆碎片
      this.tryUnlockMemoryFragment(target.type);
    }

    // 更新角色状态
    if (target.type === 'grandma') {
      this.grandmaState.currentActivity = 'chatting';
    } else if (target.type === 'daye') {
      this.dayeState.currentActivity = 'chatting';
    } else if (target.type === 'dandan') {
      this.dandanState.lastInteraction = Date.now();
      this.dandanState.mood = 'happy';
    }

    return {
      success: true,
      message: talkData.text,
      rewards: talkData.rewards
    };
  }

  // 休息动作
  private actionRest(): ActionResult {
    this.huanhuanState.energy = Math.min(100, this.huanhuanState.energy + 30);
    this.huanhuanState.emotion = 'content';

    return {
      success: true,
      message: '😌 休息了一会儿，感觉精神多了~',
      rewards: { happiness: 3 }
    };
  }

  // 劈柴动作
  private actionChopWood(target?: ActionTarget): ActionResult {
    if (this.huanhuanState.energy < 25) {
      return { success: false, message: '太累了，先休息一下吧' };
    }

    this.huanhuanState.energy -= 25;

    return {
      success: true,
      message: '🪓 咚咚咚！柴火劈好了~',
      rewards: { happiness: 4, memoryPoints: 2 }
    };
  }

  // 整理动作
  private actionTidy(target?: ActionTarget): ActionResult {
    if (this.huanhuanState.energy < 15) {
      return { success: false, message: '太累了，先休息一下吧' };
    }

    this.huanhuanState.energy -= 15;

    return {
      success: true,
      message: '📦 把东西收拾得整整齐齐~',
      rewards: { happiness: 5 }
    };
  }

  // 玩耍动作
  private actionPlay(target?: ActionTarget): ActionResult {
    if (!target || target.type !== 'dandan') {
      return { success: false, message: '只能和丹丹玩耍' };
    }

    if (this.huanhuanState.energy < 20) {
      return { success: false, message: '太累了，先休息一下吧' };
    }

    this.huanhuanState.energy -= 20;
    this.dandanState.playCount++;
    this.dandanState.lastInteraction = Date.now();
    this.dandanState.mood = 'excited';
    this.dandanState.activity = 'playing';

    const playMessages = [
      '🎾 和丹丹玩抛接球，它跑得真快！',
      '🦴 丹丹叼着骨头开心地摇尾巴~',
      '🐕 追蝴蝶玩，丹丹好灵活！',
      '🤗 和丹丹追逐打闹，好开心！'
    ];

    return {
      success: true,
      message: playMessages[Math.floor(Math.random() * playMessages.length)],
      rewards: { happiness: 10, memoryPoints: 3 }
    };
  }

  // 帮助动作
  private actionHelp(target?: ActionTarget): ActionResult {
    if (!target) {
      return { success: false, message: '没有指定帮助目标' };
    }

    if (this.huanhuanState.energy < 15) {
      return { success: false, message: '太累了，先休息一下吧' };
    }

    this.huanhuanState.energy -= 15;

    const helpMessages: Record<string, string> = {
      'grandma': '👵 帮奶奶做了些家务，奶奶夸你真懂事~',
      'daye': '👴 帮爷爷搬东西，爷爷露出欣慰的笑容~'
    };

    return {
      success: true,
      message: helpMessages[target.type] || '帮助完成',
      rewards: { happiness: 8, memoryPoints: 4 }
    };
  }

  // 进入房子
  private actionEnterHouse(): ActionResult {
    return {
      success: true,
      message: '🏠 回到了温暖的家~',
      rewards: { happiness: 5 }
    };
  }

  // 抚摸动作
  private actionPet(target?: ActionTarget): ActionResult {
    if (!target) {
      return { success: false, message: '没有指定抚摸目标' };
    }

    if (target.type === 'dandan') {
      this.dandanState.lastInteraction = Date.now();
      this.dandanState.mood = 'happy';
      
      return {
        success: true,
        message: '🐕 轻轻抚摸丹丹，它舒服地眯起眼睛~',
        rewards: { happiness: 8, memoryPoints: 2 }
      };
    }

    if (target.type === 'cat_pet') {
      return {
        success: true,
        message: '🐱 猫咪发出呼噜呼噜的声音~',
        rewards: { happiness: 5 }
      };
    }

    return { success: false, message: '这个目标不能抚摸' };
  }

  // 尝试解锁记忆碎片
  private tryUnlockMemoryFragment(source: string): void {
    // 这里可以添加解锁记忆碎片的逻辑
    // 触发给故事系统处理
  }

  // 获取可用的动作
  getAvailableActions(targetType: string, targetId?: string): ActionType[] {
    const actionMap: Record<string, ActionType[]> = {
      'chicken_coop': ['feed'],
      'vegetable_patch': ['water'],
      'flower': ['water'],
      'grandma': ['talk', 'help'],
      'daye': ['talk', 'help'],
      'dandan': ['talk', 'play', 'pet', 'feed'],
      'cat_pet': ['pet', 'feed'],
      'wood_pile': ['chop_wood'],
      'main_house': ['enter_house'],
      'broom': ['sweep'],
      'bamboo_chair': ['rest']
    };

    return actionMap[targetType] || ['look'];
  }

  // 更新任务提示
  updateTaskHint(hint: string): void {
    this.huanhuanState.taskHint = hint;
    this.notifyHuanhuanStateChange();
  }

  // 通知状态变化
  private notifyHuanhuanStateChange(): void {
    if (this.onHuanhuanStateChange) {
      this.onHuanhuanStateChange({ ...this.huanhuanState });
    }
  }

  // Getters
  getHuanhuanState(): HuanhuanState { return { ...this.huanhuanState }; }
  getDandanState(): DandanState { return { ...this.dandanState }; }
  getGrandmaState(): GrandmaState { return { ...this.grandmaState }; }
  getDayeState(): DayeState { return { ...this.dayeState }; }
}